function number(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function getAnchorChar(field) {
  const chars = Array.isArray(field?.chars) ? field.chars : [];
  for (const char of chars) {
    if (String(char?.text ?? '').trim()) return char;
  }
  return chars[0] || null;
}

function getCharBox(char) {
  const box = char?.__box || {};
  const position = char?.position || {};
  const size = char?.size || {};
  const x = number(box.x ?? position.bbox_x ?? position.x, 0);
  const y = number(box.y ?? position.bbox_y, 0);
  const right = number(box.x + box.width, number(position.bbox_right, x + number(size.width, 0)));
  const bottom = number(box.y + box.height, number(position.bbox_bottom, y + number(size.height, 0)));
  return {
    x,
    y,
    width: Math.max(0, right - x),
    height: Math.max(0, bottom - y),
  };
}

/*
 * Align the live HTML text to the ORIGINAL PDF glyph anchor.
 *
 * The old implementation aligned against field.bbox.x0/y0. That is not
 * always the visible ink origin of the first PDF glyph, especially when the
 * span contains leading side-bearing/whitespace or a PDF producer reports a
 * span box larger than the drawn text. This caused edited text to visibly
 * jump above/below or sideways on many templates.
 *
 * We now:
 *   1. reset to the immutable PDF field position;
 *   2. find the first non-whitespace source character;
 *   3. measure the actual live text range in the browser;
 *   4. translate the live element so its visible ink starts at that exact
 *      original character bbox position.
 *
 * Because the base position is restored on every pass, zoom/re-rendering
 * never accumulates a previous correction.
 */
export function alignEditorInkToField({ element, pageElement, field, scale }) {
  if (!element || !pageElement || !field) return;
  if (!String(element.textContent || '')) return;

  const pageRect = pageElement.getBoundingClientRect();
  if (!pageRect.width || !pageRect.height) return;

  const anchorChar = getAnchorChar(field);
  const anchor = anchorChar ? getCharBox(anchorChar) : {
    x: number(field?.x, 0),
    y: number(field?.top, 0),
    width: number(field?.width, 0),
    height: number(field?.height, 0),
  };

  const baseLeft = number(field?.x, 0) * scale;
  const baseTop = number(field?.top, 0) * scale;

  try {
    // Never compound the correction: start from the JSON/PDF coordinates.
    element.style.left = `${baseLeft}px`;
    element.style.top = `${baseTop}px`;

    const doc = element.ownerDocument;
    const range = doc.createRange();
    range.selectNodeContents(element);
    const rects = Array.from(range.getClientRects());
    const inkRect = rects[0] || range.getBoundingClientRect();
    if (!inkRect || (!inkRect.width && !inkRect.height)) return;

    const targetLeft = pageRect.left + anchor.x * scale;
    const targetTop = pageRect.top + anchor.y * scale;
    const deltaLeft = targetLeft - inkRect.left;
    const deltaTop = targetTop - inkRect.top;

    const nextLeft = baseLeft + deltaLeft;
    const nextTop = baseTop + deltaTop;

    if (Number.isFinite(nextLeft)) element.style.left = `${nextLeft}px`;
    if (Number.isFinite(nextTop)) element.style.top = `${nextTop}px`;
  } catch {
    element.style.left = `${baseLeft}px`;
    element.style.top = `${baseTop}px`;
  }
}
